/**
 * DRPX Courier — In-App-Chat (Supabase)
 *
 * Chat-Nachrichten pro Auftrag zwischen Firma und Fahrer, inkl.
 * Bild-Upload in den `chat-bilder`-Bucket und Realtime-Subscription
 * für Live-Updates.
 *
 * @module lib/supabase/chat
 */

import { _sb } from './client.js';
import { mapChatNachrichtFromDb } from './mappers.js';
import { validateUploadFileDeep, type UploadValidationError } from '../upload.js';
import { sanitizeMultiLine } from '../sanitize.js';
import type { RealtimeChannel } from '@supabase/realtime-js';

// ════════════════════════════════════════════════════════════════════
// Interne Helper
// ════════════════════════════════════════════════════════════════════

const NO_CLIENT_ERROR = new Error('Supabase client not initialized');

/** Minimaler Payload-Typ für Realtime postgres_changes. */
interface RealtimePayload {
  new: Record<string, unknown>;
  old: Record<string, unknown>;
}

// ════════════════════════════════════════════════════════════════════
// CHAT NACHRICHTEN
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt alle Chat-Nachrichten für einen Auftrag.
 */
export async function getChatNachrichten(auftragId: string): Promise<{
  data: Array<Record<string, unknown>>;
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('auftrag_nachrichten')
      .select('*')
      .eq('auftrag_id', auftragId)
      .order('created_at', { ascending: true });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const mapped = rows
      .map((row) => mapChatNachrichtFromDb(row))
      .filter((row): row is Record<string, unknown> => row !== null);
    return { data: mapped };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Sendet eine Chat-Nachricht in einem Auftrag.
 *
 * Wird via `apiSilent()` und auch direkt aufgerufen.
 */
export async function sendChatNachricht(
  auftragId: string,
  firmaId: string,
  fahrerId: string,
  senderId: string,
  senderName: string,
  text: string,
  bildUrl?: string,
): Promise<{ data: Record<string, unknown> | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    // Audit-083 (16.05.2026, Befund #1): Sanitisierung des Texts.
    // sanitizeMultiLine: Trim, CRLF→LF, Tabs→Space, Control-Chars raus,
    // Cap auf 5000 (DB-CHECK-Constraint). Wenn nach Sanitize leer und
    // kein Bild dabei: defensiver Reject (Frontend filtert auch schon
    // leere Texte, aber DB-CHECK lässt leeres "" und whitespace-only
    // technisch durch).
    const cleanText = sanitizeMultiLine(text, 5000);
    if (!cleanText && !bildUrl) {
      return { data: null, error: { message: 'Leere Nachricht ohne Bild' } };
    }
    const d = {
      auftrag_id: auftragId,
      firma_id: firmaId,
      fahrer_id: fahrerId,
      sender_id: senderId,
      sender_name: senderName,
      text: cleanText,
      bild_url: bildUrl || null,
    };
    const r = await client.from('auftrag_nachrichten').insert(d).select().single();
    return {
      data: r.data ? mapChatNachrichtFromDb(r.data as Record<string, unknown>) : null,
      error: r.error,
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Markiert alle Nachrichten eines Auftrags-Chats als gelesen (für Firma
 * oder Fahrer). Fire-and-forget.
 *
 * @param rolle Seite im AUFTRAG ('firma' = Auftraggeber, 'fahrer' =
 *   Auftragnehmer) — nicht die Rolle des Kontos (eine Spedition ist je nach
 *   Auftrag beides). Siehe `meineAuftragsSeite` in `lib/zugang.ts`.
 */
export async function markChatGelesen(
  auftragId: string,
  rolle: 'firma' | 'fahrer',
): Promise<void> {
  if (!_sb) return;
  const client = _sb;
  try {
    const update =
      rolle === 'firma' ? { gelesen_von_firma: true } : { gelesen_von_fahrer: true };
    const filter = rolle === 'firma' ? 'gelesen_von_firma' : 'gelesen_von_fahrer';
    await client
      .from('auftrag_nachrichten')
      .update(update)
      .eq('auftrag_id', auftragId)
      .eq(filter, false);
  } catch {
    /* ignorieren */
  }
}

/**
 * Zählt ungelesene Nachrichten pro Auftrag (für Badge-Anzeige).
 *
 * 17.10.2026: Beide Seiten — Aufträge, die das Konto ausgeschrieben hat
 * (`firma_id`), UND Aufträge, die es fährt (`fahrer_id`). Vorher richtete sich
 * die Seite nach der Rolle; eine Spedition sah Chats zu Aufträgen, die sie
 * fährt, nie als ungelesen. „Ungelesen" heißt: die eigene Seite hat noch
 * nicht gelesen, und die Nachricht kommt von der anderen Seite
 * (`sender_seite`, Migration 140; ohne Seite: nicht von mir/Firmenkonto).
 *
 * @param kontoId  Firma bzw. Fahrer (beim Mitarbeiter die Firma)
 * @param personId Beim Mitarbeiter die eigene Konto-ID, sonst weglassen
 */
export async function getUngeleseneChatsProAuftrag(
  kontoId: string,
  personId?: string,
): Promise<{ data: Record<string, number>; error?: unknown }> {
  if (!_sb) return { data: {}, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    // select('*'): läuft auch, bevor Migration 140 die Spalte sender_seite anlegt.
    const r = await client
      .from('auftrag_nachrichten')
      .select('*')
      .or(
        'and(firma_id.eq.' + kontoId + ',gelesen_von_firma.eq.false),' +
        'and(fahrer_id.eq.' + kontoId + ',gelesen_von_fahrer.eq.false)',
      );
    const rows = (r.data || []) as Array<{
      auftrag_id: string;
      firma_id: string;
      sender_id: string;
      sender_seite?: string | null;
    }>;
    const counts: Record<string, number> = {};
    rows.forEach((x) => {
      const meineSeite = x.firma_id === kontoId ? 'firma' : 'fahrer';
      const vonMeinerSeite = x.sender_seite
        ? x.sender_seite === meineSeite
        : x.sender_id === kontoId || (!!personId && x.sender_id === personId);
      if (vonMeinerSeite) return;
      counts[x.auftrag_id] = (counts[x.auftrag_id] || 0) + 1;
    });
    return { data: counts };
  } catch (e: unknown) {
    return { data: {}, error: e };
  }
}

/**
 * Lädt alle Chat-Nachrichten des Kontos (letzte zuerst) — auf beiden Seiten:
 * als Auftraggeber (`firma_id`) und als Auftragnehmer (`fahrer_id`), siehe
 * 17.10.2026 oben. Ein Query statt N+1. Für Chat-Inbox.
 *
 * @param kontoId Firma bzw. Fahrer (beim Mitarbeiter die Firma)
 */
export async function getChatUebersicht(
  kontoId: string,
): Promise<{ data: Array<Record<string, unknown>>; error?: unknown }> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('auftrag_nachrichten')
      .select('*')
      .or('firma_id.eq.' + kontoId + ',fahrer_id.eq.' + kontoId)
      .order('created_at', { ascending: false })
      .limit(500);
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const mapped = rows
      .map((row) => mapChatNachrichtFromDb(row))
      .filter((row): row is Record<string, unknown> => row !== null);
    return { data: mapped };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// CHAT-BILDER
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt ein Bild in den `chat-bilder` Storage-Bucket hoch.
 *
 * Wird über `api()` aufgerufen — daher `{data, error}`-Shape.
 *
 * **Bug-Fix gegenüber JS-Version:** Die alte Version gab `{path, error}`
 * zurück → `api()` suchte `res.data` → `null` → Caller konnte
 * `upload.path` nicht lesen → Chat-Bild-Upload schlug fehl.
 */
export async function uploadChatBild(
  auftragId: string,
  file: File,
): Promise<{ data: { path: string } | null; error: UploadValidationError | unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const valErr = await validateUploadFileDeep('chat-bilder', file);
    if (valErr) return { data: null, error: valErr };
    const ext =
      (file.name.split('.').pop() || 'jpg').replace(/[^a-zA-Z0-9]/g, '').slice(0, 10) ||
      'jpg';
    const path = auftragId + '/' + Date.now() + '.' + ext;
    const { error } = await client.storage
      .from('chat-bilder')
      .upload(path, file, { upsert: false });
    if (error) return { data: null, error: error };
    return { data: { path }, error: null };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Audit-083 (16.05.2026, Befund #2): Löscht ein hochgeladenes Chat-Bild
 * aus dem Bucket. Wird vom Frontend als Rollback aufgerufen, wenn nach
 * erfolgreichem uploadChatBild das anschließende sendChatNachricht
 * fehlschlägt (sonst entstehen Geister-Files ohne DB-Bezug → Storage-
 * Kosten + DSGVO-relevant).
 *
 * Best-effort: Fehler werden geloggt, nicht propagiert — der eigentliche
 * Send-Fehler ist die wichtigere Info für den User.
 */
export async function deleteChatBild(path: string): Promise<void> {
  if (!_sb || !path) return;
  const client = _sb;
  try {
    const r = await client.storage.from('chat-bilder').remove([path]);
    if (r.error) console.warn('[chat] deleteChatBild rollback:', r.error.message);
  } catch (e: unknown) {
    console.warn('[chat] deleteChatBild rollback:', e);
  }
}

/**
 * Generiert eine Signed URL für ein Chat-Bild (1h gültig).
 */
export async function getSignedChatBildUrl(path: string): Promise<{
  url: string | null;
  error?: unknown;
}> {
  if (!_sb) return { url: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.storage.from('chat-bilder').createSignedUrl(path, 3600);
    return { url: r.data ? r.data.signedUrl : null };
  } catch (e: unknown) {
    return { url: null, error: e };
  }
}

/**
 * Batch: Generiert Signed URLs für mehrere Chat-Bild-Pfade.
 */
export async function getSignedChatBildUrls(paths: string[]): Promise<{
  data: Record<string, string>;
  error?: unknown;
}> {
  if (!_sb) return { data: {}, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    if (!paths || paths.length === 0) return { data: {} };
    const r = await client.storage.from('chat-bilder').createSignedUrls(paths, 3600);
    const map: Record<string, string> = {};
    const items = (r.data || []) as Array<{ path?: string; signedUrl?: string }>;
    items.forEach((item) => {
      if (item.path && item.signedUrl) map[item.path] = item.signedUrl;
    });
    return { data: map };
  } catch (e: unknown) {
    return { data: {}, error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// REALTIME
// ════════════════════════════════════════════════════════════════════

/**
 * Abonniert Realtime-Events für neue Chat-Nachrichten eines Auftrags.
 */
export function subscribeChatNachrichten(
  auftragId: string,
  cb: (nachricht: Record<string, unknown> | null) => void,
): RealtimeChannel | null {
  if (!_sb) return null;
  return _sb
    .channel('chat:' + auftragId)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'auftrag_nachrichten',
        filter: 'auftrag_id=eq.' + auftragId,
      },
      function (p: RealtimePayload) {
        cb(mapChatNachrichtFromDb(p.new));
      },
    )
    .subscribe();
}

/**
 * Audit-083 (16.05.2026, Befund #5): Realtime-Subscription für die
 * ChatInbox-Übersicht. Hört auf ALLE neuen Nachrichten und ruft cb
 * pro INSERT auf, OHNE serverseitigen auftrag_id-Filter — die RLS-
 * Policy `chat_select` filtert serverseitig sowieso auf
 * `auth.uid() = firma_id OR auth.uid() = fahrer_id`, also liefert
 * Supabase Realtime dem User nur Nachrichten, die ihn betreffen.
 *
 * Verwendung: Inbox subscribed beim Öffnen → bei jeder neuen Nachricht
 * Liste neu holen oder lokal mergen.
 */
export function subscribeChatInbox(
  cb: (nachricht: Record<string, unknown> | null) => void,
): RealtimeChannel | null {
  if (!_sb) return null;
  return _sb
    .channel('chat:inbox')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'auftrag_nachrichten',
      },
      function (p: RealtimePayload) {
        cb(mapChatNachrichtFromDb(p.new));
      },
    )
    .subscribe();
}
